/**
 * The Purchase Orders list.
 *
 * The footer line states what an order is NOT, in the shop's own terms. A purchase
 * order that quietly added stock would be a bookkeeper's problem discovered at
 * stocktake; saying it here means the user learns the rule where they are deciding
 * to create one.
 */
import { useMemo, useState } from 'react';
import { formatMoney } from '@core/money/format';
import { asMoney } from '@core/money/types';
import { usePurchaseOrders } from './usePurchaseOrders';

const statusLabel = (status: string) =>
  status === 'DRAFT' ? 'Draft' : status === 'ISSUED' ? 'Issued' : status.charAt(0) + status.slice(1).toLowerCase();

export function PurchaseOrderListScreen() {
  const query = usePurchaseOrders();
  const [search, setSearch] = useState('');
  const needle = search.trim().toLowerCase();
  // One substring match over number, date, supplier and status. Four separate
  // filters would be a search engine this list does not need.
  const documents = useMemo(
    () =>
      (query.data?.documents ?? []).filter((document) => {
        const supplier = query.data?.partyNamesByDocumentId.get(document.id) ?? '';
        return `${document.docNumber} ${document.docDate} ${supplier} ${document.status}`
          .toLowerCase()
          .includes(needle);
      }),
    [query.data, needle],
  );

  return <div className="flex min-h-full flex-col gap-4 p-4 text-slate-100">
    <header className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h1 className="text-lg font-semibold">Purchase Orders</h1>
        <p className="text-xs text-slate-400">
          What we have ordered from a supplier. Issuing an order moves no stock and creates
          no payable — goods are received later.
        </p>
      </div>
      <a className="rounded bg-brand-500 px-3 py-2 text-sm font-medium" href="#/purchases/orders/new" data-testid="new-purchase-order">
        New purchase order
      </a>
    </header>
    <input
      aria-label="Search purchase orders"
      data-testid="purchase-order-search"
      className="max-w-md rounded border border-slate-700 bg-slate-900 px-3 py-2 text-sm"
      placeholder="Search number, date, supplier or status"
      value={search}
      onChange={(event) => setSearch(event.target.value)}
    />
    {query.isLoading && <p className="text-sm text-slate-400">Loading purchase orders…</p>}
    {query.isError && <p role="alert" className="text-sm text-red-300">Could not load purchase orders: {query.error.message}</p>}
    {!query.isLoading && documents.length === 0 && (
      <p className="rounded border border-slate-800 p-4 text-sm text-slate-400">
        {search ? 'No matching purchase orders.' : 'No purchase orders yet.'}
      </p>
    )}
    {documents.length > 0 && (
      <div className="overflow-x-auto rounded border border-slate-800">
        <table className="w-full text-sm">
          <thead className="bg-slate-900 text-left text-xs uppercase text-slate-400">
            <tr><th className="p-3">Order</th><th className="p-3">Date</th><th className="p-3">Supplier</th><th className="p-3 text-right">Amount</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr>
          </thead>
          <tbody>
            {documents.map((document) => (
              <tr key={document.id} data-testid="purchase-order-row" data-doc-type={document.docType} className="border-t border-slate-800">
                <td className="p-3 font-medium">{document.docNumber}</td>
                <td className="p-3">{document.docDate}</td>
                <td className="p-3">{query.data?.partyNamesByDocumentId.get(document.id) ?? document.partySnapshot.name}</td>
                <td className="p-3 text-right">{formatMoney(asMoney(document.grandTotalPaise))}</td>
                <td className="p-3">{statusLabel(document.status)}</td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-2">
                    <a className="text-brand-400 underline" href={`#/purchases/orders/${document.id}`}>Open / Preview</a>
                    {document.status === 'DRAFT' && (
                      <a className="text-brand-400 underline" href={`#/purchases/orders/${document.id}/edit`}>Edit draft</a>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
    <p className="text-xs text-slate-500">
      {documents.length} shown · {query.data?.total ?? 0} purchase orders
    </p>
  </div>;
}